import React, { useState, useEffect, useRef, ChangeEvent, KeyboardEvent } from "react";

import "../ChatStyle.less";
import { NewMessage } from "../ChatInterfaces";
import { ChatSettingsState } from "../../../../../appRedux/reduxInterfaces/ReducersInterfaces";

interface TextareaWithOptionsProps {
   options: string[];
   newMessage: NewMessage;
   setNewMessage: React.Dispatch<React.SetStateAction<NewMessage>>;
   chatSettings: ChatSettingsState;
   intl: { formatMessage: (msg: { id: string }) => string };
   onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
   onKeyPressed: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const TextareaWithOptions: React.FC<TextareaWithOptionsProps> = ({
   options = [], // Default to empty array
   newMessage,
   setNewMessage,
   chatSettings,
   intl,
   onChange,
   onKeyPressed
}) => {
   const [showOptions, setShowOptions] = useState<boolean>(false);
   const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
   const [cursorPosition, setCursorPosition] = useState<number>(0);
   const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(-1);
   const textareaRef = useRef<HTMLTextAreaElement | null>(null);

   // Function to handle changes in the textarea input
   const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e);
      const { value } = e.target;
      const atIndex = value.lastIndexOf("@");

      if (options.length === 0) {
         // If options are empty, treat textarea as a normal input
         setShowOptions(false);
         setFilteredOptions([]);
         setSelectedOptionIndex(-1);
         return;
      }

      if (atIndex === -1) {
         // If "@" is not present, hide options
         setShowOptions(false);
         setFilteredOptions([]);
         setSelectedOptionIndex(-1);
         return;
      }

      const textAfterAt = value
         .substring(atIndex + 1)
         .split(" ")[0]
         .trim();

      if (textAfterAt === "") {
         // Show options if there is nothing or only spaces after "@"
         setFilteredOptions(options);
         setShowOptions(true);
         setSelectedOptionIndex(-1);
         setCursorPosition(atIndex + 1);
      } else {
         // Filter options based on the text after "@"
         const filtered = options.filter((option) =>
            option.toLowerCase().startsWith(textAfterAt.toLowerCase())
         );
         setFilteredOptions(filtered);
         setShowOptions(filtered.length > 0);
         setCursorPosition(atIndex + 1 + textAfterAt.length);
      }

      // Check if there is text after the selected option
      const textAfterOption = value.substring(atIndex + 1 + textAfterAt.length).trim();
      if (textAfterOption.length > 0) {
         // Hide options if there is additional text after the "@option"
         setShowOptions(false);
         setFilteredOptions([]);
         setSelectedOptionIndex(-1);
      }
   };

   // Function to handle option click from the dropdown
   const handleOptionClick = (option: string) => {
      if (textareaRef.current && newMessage.content !== undefined) {
         const { content } = newMessage;
         const atIndex = content.lastIndexOf("@");
         const newValue = content.substring(0, atIndex + 1) + option + " ";
         setNewMessage({ ...newMessage, content: newValue });
         setShowOptions(false);
         setSelectedOptionIndex(-1);
         textareaRef.current.focus();
      }
   };

   // Function to handle keydown events in the textarea
   const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
         onKeyPressed(e);
         document.getElementById("submit-525")?.click();
         e.preventDefault();
         setShowOptions(false);
         setSelectedOptionIndex(-1);
      } else if (showOptions) {
         if (e.key === "ArrowDown" || e.key === "Tab") {
            e.preventDefault();
            setSelectedOptionIndex((prevIndex) => (prevIndex + 1) % filteredOptions.length);
         } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedOptionIndex(
               (prevIndex) => (prevIndex - 1 + filteredOptions.length) % filteredOptions.length
            );
         } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            if (selectedOptionIndex >= 0) {
               handleOptionClick(filteredOptions[selectedOptionIndex]);
            }
         }
      }
   };

   // Effect to update the message content and hide options when an option is selected
   useEffect(() => {
      if (selectedOptionIndex >= 0 && showOptions && filteredOptions.length > 0) {
         const selectedOption = filteredOptions[selectedOptionIndex];
         const { content } = newMessage;
         const atIndex = content.lastIndexOf("@");
         const newValue = content.substring(0, atIndex + 1) + selectedOption + " ";
         setNewMessage({ ...newMessage, content: newValue });
         setShowOptions(false);
         setSelectedOptionIndex(-1);
         if (textareaRef.current) {
            textareaRef.current.focus();
         }
      }
   }, [selectedOptionIndex]);

   return (
      <>
         <textarea
            ref={textareaRef}
            name="content"
            className="text-area-with-options"
            id="content"
            rows={2}
            autoComplete="off"
            value={newMessage.content || ""}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={chatSettings?.currentRoom?.id === undefined}
            placeholder={intl.formatMessage({ id: "chat.writeMessage" })}
         />
         {showOptions && filteredOptions.length > 0 && (
            <ul className="chat-input-option-list">
               {filteredOptions.map((option, index) => (
                  <button
                     key={index}
                     className={`chat-input-option-list__button ${
                        index === selectedOptionIndex ? "selected" : ""
                     }`}
                     onClick={() => handleOptionClick(option)}
                  >
                     {option}
                  </button>
               ))}
            </ul>
         )}
      </>
   );
};

export default TextareaWithOptions;
